import { computed } from "vue";
import {
  Facility,
  IBaseFilter,
  ILevelFilter,
  SeparatedLevels,
} from "~/server/types/server.interface";
import { Archived, Default } from "~/types/entities";
import { mapSubscriptionModelToLevels, SUBSCRIPTION_MODEL_TAB_LIST } from "~/constants";
import { LEVEL_PRIORITY } from "~/server/helpers/levels";

const STORE_NAMESPACE = "tableFilter";

export const PAYLOAD_TABLE_LEVELS = [
  [Archived.REGION, Archived.SILVER, Archived.GOLD, Archived.PLATINUM],
  [Default.REGION, Default.LITE, Default.CLASSIC, Default.PREMIUM, Default.VIP],
];

const defaultSubscriptionModelLevel = {
  archived: { id: "", value: "", isAvailable: false, reason: "" },
  default: { id: "", value: "", isAvailable: false, reason: "" }
};

export type TableFilterStore = {
  subscriptionModel: globalThis.Ref<number>,

  facilities: globalThis.Ref<Facility[]>,

  availableLevelsGetter: globalThis.ComputedRef<ILevelFilter[]>,
  availableLevelsByTypeGetter: globalThis.ComputedRef<SeparatedLevels>,
  selectedLevels: globalThis.Ref<SeparatedLevels>,
  setAvailableLevels: (levels: SeparatedLevels) => void,
  setSelectedLevel: (level: ILevelFilter) => void,
  setSelectedLevelsByType: (level: SeparatedLevels) => void,
  setSelectedLevels: (defaultSelectedLevelsValue: ILevelFilter[]) => void,
  getSelectedLevelIds: () => string[],

  translatedActivities: globalThis.Ref<ILevelFilter[]>,
  selectedActivities: globalThis.Ref<ILevelFilter[]>,
  setSelectedActivities: (payloadActivity: ILevelFilter) => void,
  resetSelectedActivities: () => void,
  prepareSelectedActivities: () => string[],

  search: globalThis.Ref<string>,
  searchActivities: globalThis.Ref<string>,

  isLoadingData: globalThis.Ref<boolean>,

  citiesList: globalThis.Ref<ILevelFilter[]>,
  selectedCity: globalThis.ComputedRef<ILevelFilter | undefined>,
  setSelectedCities: (payloadCity: ILevelFilter) => void,
  setCitiesList: (payloadCity: ILevelFilter[]) => void,
  resetSelectedCity: () => void,
  selectedCities: globalThis.Ref<ILevelFilter[]>,

  tags: globalThis.Ref<ILevelFilter[]>,
  setTags: (payloadTag: ILevelFilter[]) => void,
  selectedTags: globalThis.Ref<ILevelFilter[]>,
  setSelectedTags: (payloadTag: ILevelFilter) => void,
  resetSelectedTags: () => void,
  subscriptionModelTabOptions: globalThis.ComputedRef<IBaseFilter[]>,
  subscriptionModelTabValue: globalThis.ComputedRef<IBaseFilter>,
  setSubscriptionModelTabValue: (newTabValue: IBaseFilter) => void,
  selectedLevelsBySubscriptionModel: globalThis.ComputedRef<ILevelFilter[]>,
}

export const useTableFilterStore = defineStore(STORE_NAMESPACE, (): TableFilterStore => {
  const { t } = useI18n();
  const facilities = ref<Facility[]>([]);
  const search = ref("");
  const searchActivities = ref("");
  const subscriptionModel = ref(1);
  const isLoadingData = ref(true);

  const levels = ref<SeparatedLevels>({ archived: [], default: [] });
  const selectedLevels = ref<SeparatedLevels>({ archived: [], default: [] });
  const levelTypeBySubscriptionModel = computed(() => mapSubscriptionModelToLevels[subscriptionModel.value]);
  const selectedLevelsBySubscriptionModel = computed(() => {
    return selectedLevels.value[levelTypeBySubscriptionModel.value];
  });
  const tabValue = ref<IBaseFilter>(SUBSCRIPTION_MODEL_TAB_LIST[0]); // there's should be selected 'Актуальные' tab by default;

  const availableLevelsGetter = computed<ILevelFilter[]>(
    () => {
      return levels.value[levelTypeBySubscriptionModel.value];
    }
  );

  const availableLevelsByTypeGetter = computed<SeparatedLevels>(() => levels.value);

  const subscriptionModelTabOptions = computed(() => {
    return SUBSCRIPTION_MODEL_TAB_LIST;
  });

  const subscriptionModelTabValue = computed(() => {
    return SUBSCRIPTION_MODEL_TAB_LIST[subscriptionModel.value === 1 ? 0 : 1];
  });

  const setSubscriptionModelTabValue = (newTabValue: IBaseFilter) => {
    tabValue.value = newTabValue;
    subscriptionModel.value = Number(newTabValue.id);
    const isLevelsSelected = selectedLevelsBySubscriptionModel.value.length;
    
    setSelectedLevels(
      isLevelsSelected 
        ? selectedLevelsBySubscriptionModel.value 
        : availableLevelsGetter.value
    );
  };

  const setAvailableLevels = (availableLevels: SeparatedLevels) => {
    levels.value = availableLevels;
  };

  const setSelectedLevels = (defaultSelectedLevelsValue: ILevelFilter[]) => {
    selectedLevels.value[levelTypeBySubscriptionModel.value] = [...defaultSelectedLevelsValue];
  };

  const setSelectedLevelsByType = (defaultSelectedLevelsValue: SeparatedLevels) => {
    selectedLevels.value.archived = [...defaultSelectedLevelsValue.archived];
    selectedLevels.value.default = [...defaultSelectedLevelsValue.default];
  };

  const getSelectedLevelIds = (): string[] => {
    return selectedLevels.value[levelTypeBySubscriptionModel.value].map((level) => level.id);
  };

  const setSelectedLevel = (level: ILevelFilter) => {
    const levelIndex = selectedLevelsBySubscriptionModel.value.findIndex((item) => item.id === level.id);
    const alreadySelected = levelIndex !== -1;
    const moreThanOne = selectedLevels.value[levelTypeBySubscriptionModel.value].length > 1;

    if (alreadySelected && moreThanOne) {
      selectedLevels.value[levelTypeBySubscriptionModel.value].splice(levelIndex, 1);
    } else {
      const levelPriorityIndex = selectedLevelsBySubscriptionModel.value.findIndex(
        item =>
          LEVEL_PRIORITY[subscriptionModel.value][item.id] > LEVEL_PRIORITY[subscriptionModel.value][level.id]
      );
      if (levelPriorityIndex === -1) {
        const theOnlyOne = selectedLevels.value[levelTypeBySubscriptionModel.value][0].id === level.id;

        if (theOnlyOne) {
          return;
        }

        selectedLevels.value[levelTypeBySubscriptionModel.value].push(level);
      } else {
        selectedLevels.value[levelTypeBySubscriptionModel.value].splice(levelPriorityIndex, 0, level);
      }
    }
  };

  const translatedActivities = ref<ILevelFilter[]>([]);
  const selectedActivities = ref<ILevelFilter[]>([]);

  const setSelectedActivities = (payloadActivity: ILevelFilter): void => {
    const payloadActivityIndex = selectedActivities.value.findIndex(
      (item) => item.id === payloadActivity.id
    );

    if (payloadActivityIndex !== -1) {
      selectedActivities.value?.splice(payloadActivityIndex, 1);
    } else {
      selectedActivities.value.push(payloadActivity);
    }
  };

  const resetSelectedActivities = (): void => {
    selectedActivities.value = [];
  };

  const prepareSelectedActivities = (): string[] => {
    return selectedActivities.value.map((activity) => activity.id);
  };

  const citiesList = ref<ILevelFilter[]>([]);
  const selectedCities = ref<ILevelFilter[]>([]);
  // For now, we do not allow to choose more than 1 city at once in filters
  const selectedCity = computed<ILevelFilter | undefined>(() => selectedCities.value[0]);

  const setSelectedCities = (payloadCity: ILevelFilter): void => {
    selectedCities.value[0] = payloadCity;
  };

  const setCitiesList = (cities: ILevelFilter[]) => {
    citiesList.value = cities;
  };

  const resetSelectedCity = () => {
    selectedCities.value[0] = citiesList.value[0];
  };

  const tags = ref<ILevelFilter[]>([]);
  const selectedTags = ref<ILevelFilter[]>([]);

  const setTags = (allTags: ILevelFilter[]) => {
    tags.value = allTags;
  };

  const setSelectedTags = (payloadTag: ILevelFilter): void => {
    const payloadTagIndex = selectedTags.value.findIndex(
      (item) => item.id === payloadTag.id
    );

    if (payloadTagIndex !== -1) {
      selectedTags.value?.splice(payloadTagIndex, 1);
    } else {
      selectedTags.value.push(payloadTag);
    }
  };

  const resetSelectedTags = (): void => {
    selectedTags.value = [];
  };

  return {
    facilities,

    availableLevelsGetter,
    selectedLevels,
    setAvailableLevels,
    setSelectedLevel,
    setSelectedLevels,
    getSelectedLevelIds,

    availableLevelsByTypeGetter,
    setSelectedLevelsByType,

    selectedActivities,
    translatedActivities,
    setSelectedActivities,
    resetSelectedActivities,
    prepareSelectedActivities,

    search,
    searchActivities,

    isLoadingData,

    citiesList,
    selectedCities,
    selectedCity,
    setSelectedCities,
    setCitiesList,
    resetSelectedCity,

    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    resetSelectedTags,

    subscriptionModel,

    subscriptionModelTabOptions,
    subscriptionModelTabValue,
    selectedLevelsBySubscriptionModel,
    setSubscriptionModelTabValue,
  };
});
